import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import DownloadIcon          from "@mui/icons-material/Download";
import RefreshIcon           from "@mui/icons-material/Refresh";
import KeyboardArrowUpIcon   from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import UnfoldMoreIcon        from "@mui/icons-material/UnfoldMore";
import { alpha }             from "@mui/material/styles";
import { axios_tenant }      from "../../../axios";
import { useNavigate }       from "react-router-dom";
import moment                from "moment";

const TERMINAL_STATUSES = new Set(["Completed", "Partially Completed", "Failed"]);
const ROW_POLL_MS       = 10_000;

// ── Theme palette reference ───────────────────────────────────────────────────
// primary #010160 | info #2EB5C9 | success #3BD2A2 | error #E73145

const STATUS_COLOR = {
  "Queued":             "#8595A6",
  "In Progress":        "#475259",
  "Completed":          "#00A073",
  "Partially Completed":"#F59E0B",
  "Failed":             "#E73145",
};

const getProgressColor = (task) => {
  if (task.status === "Failed")             return "#E73145";
  if (task.status === "Completed")          return "#3BD2A2";
  if (task.status === "Partially Completed") return "#F59E0B";
  if (task.totalEntries && task.completedEntries === task.totalEntries) return "#3BD2A2";
  if (task.status === "In Progress")        return "#2EB5C9";
  return "#A2B2C3";
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ProgressCell = ({ task }) => {
  const color   = getProgressColor(task);
  const total   = task.totalEntries;
  const done    = task.completedEntries + task.failedEntries;
  const pct     = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const showDot = task.status === "In Progress" && task.completedEntries < total;

  return (
    <Box sx={{ minWidth: 100 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.4 }}>
        {showDot && (
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
        )}
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 500, color, fontVariantNumeric: "tabular-nums" }}>
          {task.completedEntries}/{task.totalEntries ?? "—"}
        </Typography>
      </Box>
      <Box sx={{ height: 2, borderRadius: 999, bgcolor: alpha(color, 0.2), overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${pct}%`, bgcolor: color, borderRadius: 999, transition: "width 0.3s ease" }} />
      </Box>
    </Box>
  );
};

const BTN_BASE = { textTransform: "none", fontWeight: 500, p: 0, minWidth: 0, whiteSpace: "nowrap" };

const ActionButton = ({ task, onDownload, onErrorDownload }) => {
  if (task.hasDownload) {
    return (
      <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
        onClick={() => onDownload(task.id)}
        sx={{ ...BTN_BASE, color: "#010160", "&:hover": { bgcolor: "transparent", textDecoration: "underline" } }}>
        Excel
      </Button>
    );
  }
  if (task.status === "Failed") {
    if (task.hasErrorDownload) {
      return (
        <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
          onClick={() => onErrorDownload(task.id)}
          sx={{ ...BTN_BASE, color: "#E73145", "&:hover": { bgcolor: "transparent", textDecoration: "underline" } }}>
          Error File
        </Button>
      );
    }
    return (
      <Tooltip title={task.errorMessage || "Error report not yet available"}>
        <span>
          <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
            disabled
            sx={{ ...BTN_BASE, "&.Mui-disabled": { color: "#E7314580" } }}>
            Error File
          </Button>
        </span>
      </Tooltip>
    );
  }
  return (
    <Button size="small" startIcon={<DownloadIcon sx={{ fontSize: "14px !important" }} />}
      disabled
      sx={{ ...BTN_BASE, "&.Mui-disabled": { color: "#A2B2C3" } }}>
      Download
    </Button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const TrackTask = () => {
  const navigate = useNavigate();

  const [tasks,      setTasks]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error,      setError]      = useState(null);
  const [sortBy,     setSortBy]     = useState("submittedAt");
  const [sortDir,    setSortDir]    = useState("desc");

  const pollIntervalRef = useRef(null);
  const activeIdsRef    = useRef(new Set());

  const stopPolling = useCallback(() => {
    clearInterval(pollIntervalRef.current);
    pollIntervalRef.current = null;
  }, []);

  const startRowPolling = useCallback((tasksList) => {
    stopPolling();

    activeIdsRef.current = new Set(
      tasksList.filter(t => !TERMINAL_STATUSES.has(t.status)).map(t => t.id)
    );

    if (activeIdsRef.current.size === 0) return;

    pollIntervalRef.current = setInterval(async () => {
      // Skip tick if tab is backgrounded — resume automatically on next tick when visible.
      if (document.visibilityState !== "visible") return;

      const ids = [...activeIdsRef.current];
      if (ids.length === 0) { stopPolling(); return; }

      const results = await Promise.allSettled(
        ids.map(id => axios_tenant.get(`/adim/track-tasks/${id}`))
      );

      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const updated = result.value.data?.task;
        if (!updated) return;

        setTasks(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));

        if (TERMINAL_STATUSES.has(updated.status)) {
          activeIdsRef.current.delete(updated.id);
        }
      });

      if (activeIdsRef.current.size === 0) stopPolling();
    }, ROW_POLL_MS);
  }, [stopPolling]);

  const fetchTasks = useCallback(async ({ isRefresh = false } = {}) => {
    if (isRefresh) setRefreshing(true);
    try {
      const { data } = await axios_tenant.get("/adim/track-tasks");
      const tasksList = data.tasks ?? [];
      setTasks(tasksList);
      setError(null);
      startRowPolling(tasksList);
    } catch {
      setError("Failed to load tasks. Please try again.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [startRowPolling]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // Stop polling on unmount.
  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleDownload = async (taskId) => {
    try {
      const { data } = await axios_tenant.get(`/adim/track-tasks/${taskId}/download`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      setError("Failed to download the file. Please try again.");
    }
  };

  const handleErrorDownload = async (taskId) => {
    try {
      const { data } = await axios_tenant.get(`/adim/track-tasks/${taskId}/error-report`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      setError("Failed to download the error report. Please try again.");
    }
  };

  // ── Sorting ────────────────────────────────────────────────────────────────

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const sortedTasks = [...tasks].sort((a, b) => {
    const cmp = String(a[sortBy] ?? "").localeCompare(String(b[sortBy] ?? ""), undefined, { numeric: true });
    return sortDir === "asc" ? cmp : -cmp;
  });

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <UnfoldMoreIcon sx={{ fontSize: 14, color: "#A2B2C3", ml: 0.5 }} />;
    return sortDir === "asc"
      ? <KeyboardArrowUpIcon   sx={{ fontSize: 14, color: "#475259", ml: 0.5 }} />
      : <KeyboardArrowDownIcon sx={{ fontSize: 14, color: "#475259", ml: 0.5 }} />;
  };

  const columns = [
    { id: "id",          label: "Task ID",                 width: 55  },
    { id: "requestType", label: "Request Type",            width: 155 },
    { id: "submittedBy", label: "Submitted By",            width: 148 },
    { id: "submittedAt", label: "Submission Date & Time",  width: 148 },
    { id: "status",      label: "Current Status",          width: 85  },
    { id: "progress",    label: "Progress",  noSort: true, width: 100 },
    { id: "completedAt", label: "Completion Date & Time",  width: 148 },
    { id: "action",      label: "Action",    noSort: true, width: 70  },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 2, py: 3 }}>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Typography variant="h3">View Task History</Typography>
        <Tooltip title="Refresh">
          <span>
            <IconButton size="small" onClick={() => fetchTasks({ isRefresh: true })} disabled={refreshing}
              sx={{ color: "primary.main", "&:hover": { bgcolor: "transparent" } }}>
              <RefreshIcon fontSize="small"
                sx={{ ...(refreshing && { animation: "spin 0.7s linear infinite" }) }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 260px)" }}>
          <Table size="small" stickyHeader>

            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell key={col.id}
                    onClick={!col.noSort ? () => handleSort(col.id) : undefined}
                    sx={{
                      width: col.width,
                      cursor: col.noSort ? "default" : "pointer",
                      userSelect: "none",
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                      fontSize: "0.78rem",
                      color: sortBy === col.id ? "primary.main" : "text.primary",
                      bgcolor: "background.paper",
                      borderBottom: "2px solid",
                      borderColor: "divider",
                      py: 1.5,
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {col.label}
                      {!col.noSort && <SortIcon col={col.id} />}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 8, color: "text.disabled" }}>
                    No tasks found.
                  </TableCell>
                </TableRow>
              ) : (
                sortedTasks.map(task => (
                  <TableRow key={task.id} hover sx={{ "&:last-child td": { borderBottom: "none" } }}>

                    <TableCell sx={{ py: 1.5, width: 55 }}>
                      <Tooltip title={task.id} placement="top">
                        <Typography sx={{ fontSize: "0.8rem", color: "text.secondary", fontFamily: "monospace",
                          maxWidth: 50, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {task.id}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, width: 155 }}>
                      <Typography sx={{ fontSize: "0.82rem", color: "text.primary", wordBreak: "break-word" }}>
                        {task.requestType}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, width: 148 }}>
                      <Tooltip title={task.submittedBy} placement="top">
                        <Typography sx={{ fontSize: "0.82rem", color: "text.primary",
                          maxWidth: 138, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {task.submittedBy}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, width: 148, whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.82rem", color: "text.primary", fontVariantNumeric: "tabular-nums" }}>
                        {task.submittedAt ? moment(task.submittedAt).format("MMM D, YYYY h:mm A") : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, width: 85 }}>
                      <Typography sx={{ fontSize: "0.82rem", color: STATUS_COLOR[task.status] ?? "text.primary" }}>
                        {task.status}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, width: 100 }}>
                      <ProgressCell task={task} />
                    </TableCell>

                    <TableCell sx={{ py: 1.5, width: 148, whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.82rem", color: "text.primary", fontVariantNumeric: "tabular-nums" }}>
                        {task.completedAt ? moment(task.completedAt).format("MMM D, YYYY h:mm A") : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ py: 1.5, width: 70 }}>
                      <ActionButton task={task} onDownload={handleDownload} onErrorDownload={handleErrorDownload} />
                    </TableCell>

                  </TableRow>
                ))
              )}
            </TableBody>

          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
        <Button variant="contained" color="primary" onClick={() => navigate("/adim/process-matter")}
          sx={{ px: 4, fontWeight: 600 }}>
          Continue
        </Button>
      </Box>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

    </Box>
  );
};

export default TrackTask;
